/**
 * User-facing usage vocabulary (2026-08-15 Miles directive).
 *
 * Canonical terms on every usage surface (activity page, wallet, chat ⓘ):
 *   • "task"  — one user request end-to-end (never "turn"/"request")
 *   • "step"  — one model call within a task (never "call")
 *
 * These maps normalize at DISPLAY time so historical rows written with the
 * old copy render the current vocabulary too. Internal identifiers (source
 * slugs, field names, doc ids) are never rewritten — display only.
 */

/** Friendly display label for an activity/expense `source` slug. */
const SOURCE_LABELS: Record<string, string> = {
  'prest0n-session': 'chat task',
  'prest0n-session-vm': 'chat task',
  'prest0n-memory': 'memory update',
  'thread-followup-suggestions': 'follow-up suggestions',
  'greeting-suggestions': 'greeting suggestions',
  'deep-research': 'deep research',
  'deep-research-composer': 'deep research',
  'document-analysis': 'document analysis',
  'widget-chat': 'widget chat',
  'server-orchestrator': 'chat task',
  'app-tool-call': 'app tool call',
  'vm-egress': 'background agent work',
  'read-aloud': 'read aloud',
  // P3 (2026-09-16): Prest0n's own work, named in the words a founder uses —
  // every one of these used to render as "chat task".
  'prest0n-triage': "Prest0n's engineering check-in",
  'prest0n-hub': "Prest0n's engineering hub report",
  'prest0n-routine': 'routine run',
  'prest0n-platform': 'platform task',
  'coding-run': 'coding run',
  'decision': 'routing decision',
};

/**
 * Work the ACCOUNT did on its own, rather than work a person asked for.
 *
 * INCIDENT 2026-09-10: 53% of the burn on the wallet that got an unexpected
 * $500 charge was work its owner never initiated — "background agent work",
 * follow-up suggestions, memory updates. All of it legitimate, none of it
 * legible: the expenses list showed one flat stream, so there was no way to see
 * that half the money went to things nobody clicked.
 *
 * MIRROR of BACKGROUND_EXPENSE_SOURCES in functions/src/lib/memberUsageRollup.ts,
 * whose header says "keep this the ONE list". Pinned by
 * scripts/check-frontend-constant-mirrors.mjs, because a mirror without a gate
 * is a drift waiting to happen — and a row that silently changes category is
 * worse than no category at all.
 */
export const BACKGROUND_EXPENSE_SOURCES: readonly string[] = Object.freeze([
  'vm-egress',
  'greeting-suggestions',
  'prest0n-memory',
  'prest0n-ledger',
  'thread-followup-suggestions',
  'read-aloud',
  // P3 (2026-09-16): Prest0n's own work, by purpose — MIRROR of the server list.
  'prest0n-triage',
  'prest0n-hub',
  'prest0n-routine',
  'prest0n-platform',
  'coding-run',
  'decision',
]);

/**
 * Fail-open, exactly like the server's `isInteractiveTurnSource`: an unknown
 * source counts as a task someone asked for. Mislabelling real work as
 * background would hide it from the person who is paying for it, which is the
 * more expensive mistake.
 */
export function isBackgroundExpenseSource(source?: string | null): boolean {
  return BACKGROUND_EXPENSE_SOURCES.includes(String(source || '').trim());
}

export function friendlySourceLabel(source?: string | null): string {
  const slug = (source || '').trim().toLowerCase();
  if (!slug) return 'unknown';
  return SOURCE_LABELS[slug] || slug.replace(/-/g, ' ');
}

/**
 * Normalize a row summary to the task/step vocabulary — historical rows say
 * "webchat turn", "session turn", "memory extraction (post-turn)" etc.
 *
 * 2026-08-16: summaries are now the task's own words (first words of the
 * user's request), so the blanket turn→task rewrite only applies to strings
 * that look like the OLD system-generated copy — a user asking to "turn off
 * the alarm" must not render as "task off the alarm".
 */
const LEGACY_SUMMARY_PATTERN =
  /(webchat turn|dedicated-vm|session turn|memory extraction|account chat|widget turn)/i;

export function friendlySummary(summary?: string | null): string {
  const raw = (summary || '').trim();
  if (!raw) return '';
  if (!LEGACY_SUMMARY_PATTERN.test(raw)) return raw;
  return raw
    .replace(/\bwebchat turn\b/gi, 'chat task')
    .replace(/\bdedicated-vm live turn\b/gi, 'chat task (live)')
    .replace(/\bdedicated-vm turn\b/gi, 'chat task')
    .replace(/\bsession turn\b/gi, 'chat task')
    // No trailing \b: after ')' there is no word boundary, so the anchored
    // variant never matched and the blanket turn→task rewrite mangled it.
    .replace(/\bmemory extraction \(post-turn\)/gi, 'memory update (after task)')
    .replace(/\bturns\b/gi, 'tasks')
    .replace(/\bturn\b/gi, 'task');
}

/**
 * Background-work stage → display label (thread-ledger PR-5, 2026-08-27).
 * The server stamps `stage` on background steps ('ledger-compaction' thread
 * summary updates, 'memory-extraction' memory updates) so the activity page
 * and the ⓘ label the SAME step the SAME way — the uniformity the owner
 * asked for. House voice: lowercase.
 */
const GENERATION_STAGE_LABELS: Record<string, string> = {
  'ledger-compaction': 'thread summary update',
  'memory-extraction': 'memory update',
  // 2026-09-04: a vendor-billed picture/video/audio render — the cost is on the vendor's invoice,
  // not this ledger, so the row says so instead of showing a false $0.
  'media-render': 'billed to vendor',
  // 2026-09-10 (Miles): a narration segment rendered for a message — PRICED (the SSOT unit price,
  // "≈"), folded into the message's task on the activity page and a step on the message's ⓘ.
  'read-aloud': 'read aloud',
};

/**
 * 2026-09-08 (Miles: model usage tracked always, media included): a picture / video / audio
 * render whose vendor reports no cost is priced from its MEASURED units at the SSOT unit price
 * and stamped costSource:'estimate'. Every surface that shows its work units prefixes this
 * marker so a published-card figure is never mistaken for the vendor's invoice; a
 * vendor-reported cost carries no marker.
 */
export const COST_ESTIMATE_MARKER = '≈';
export const COST_ESTIMATE_TITLE = "estimated from the vendor's published unit price";

export function costEstimateMarker(u: { costSource?: string | null } | null | undefined): string | null {
  return u && u.costSource === 'estimate' ? COST_ESTIMATE_MARKER : null;
}

export function generationStageLabel(stage?: string | null): string | null {
  const s = (stage || '').trim().toLowerCase();
  return GENERATION_STAGE_LABELS[s] || null;
}

/**
 * ⓘ step label for a billedSeparately sidecar entry. The server-stamped
 * `stage` wins (PR-5); the sidecarKey prefix stays as the fallback for rows
 * written before the stamp existed (`memext_…` memory update, `threadfup_…`
 * follow-up suggestions, `ledger_…` thread summary update). Unknown
 * billedSeparately steps get no label (app-tool metering legs already read
 * clearly from their station name).
 */
export function auxSidecarStepLabel(
  u: { billedSeparately?: boolean; sidecarKey?: string; stage?: string } | null | undefined,
): string | null {
  if (!u || u.billedSeparately !== true) return null;
  const byStage = generationStageLabel(u.stage);
  if (byStage) return byStage;
  const key = typeof u.sidecarKey === 'string' ? u.sidecarKey : '';
  if (key.startsWith('memext')) return 'memory update';
  if (key.startsWith('threadfup')) return 'follow-up suggestions';
  if (key.startsWith('ledger_')) return 'thread summary update';
  return null;
}
